/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { Icon } from './Primitives';

const TABS = ['Inventory', 'Products', 'Tags & Labels'];

const LOW_STOCK_THRESHOLD = 8;

const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'in', label: 'In stock' },
  { key: 'low', label: 'Low stock' },
  { key: 'out', label: 'Out of stock' },
];

// Quantities aren't random — they follow explainable rules instead of a
// bare hash: unit counts vary by product type (a $700 espresso machine is
// never stocked like a bag of beans), skew down as price rises within a
// category (rarer, smaller-lot items), and each merchandising tag carries
// a real signal — Limited actually means limited, Bestseller and Sale
// items are kept well stocked, New releases start with a smaller batch.
// The per-id hash only breaks ties within those rules, it doesn't drive
// the number on its own.
const TYPE_QTY_RANGE = {
  beans: [15, 45],
  pods: [20, 60],
  machines: [3, 15],
  gear: [10, 30],
  drinkware: [15, 40],
};
const TYPE_PRICE_CEILING = { beans: 50, pods: 30, machines: 800, gear: 250, drinkware: 45 };

function seedQty(p) {
  if (!p.stock) return 0;

  const [lo, hi] = TYPE_QTY_RANGE[p.type] || [10, 30];
  const ceiling = TYPE_PRICE_CEILING[p.type] || 100;

  let h = 0;
  for (let i = 0; i < p.id.length; i++) h = (h * 31 + p.id.charCodeAt(i)) >>> 0;
  let qty = lo + (h % (hi - lo + 1));

  const priceFactor = 1 - Math.min(0.5, (p.price / ceiling) * 0.5);
  qty = Math.round(qty * priceFactor);

  if (p.tag === 'Limited') qty = 3 + (h % 6);
  if (p.tag === 'Bestseller') qty = Math.round(qty * 1.3);
  if (p.tag === 'Sale') qty = Math.round(qty * 1.25);
  if (p.tag === 'New') qty = Math.round(qty * 0.6);

  return Math.max(0, Math.min(qty, hi + Math.round(hi * 0.4)));
}

function stockStatus(qty) {
  if (qty <= 0) return 'out';
  if (qty <= LOW_STOCK_THRESHOLD) return 'low';
  return 'in';
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="admin-stat">
      <span className="admin-stat-label">{label}</span>
      <span className="admin-stat-value" style={color ? { color } : {}}>{value}</span>
      {sub && <span className="admin-stat-sub">{sub}</span>}
    </div>
  );
}

function StockBadge({ qty }) {
  const status = stockStatus(qty);
  const label = status === 'out' ? 'Out of stock' : status === 'low' ? 'Low stock' : 'In stock';
  return <span className={`admin-stock-badge ${status}`}>{label}</span>;
}

export default function Admin({ data, onClose }) {
  const [tab, setTab] = useState('Inventory');
  const [products, setProducts] = useState(data.products.map(p => ({ ...p, qty: seedQty(p) })));
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortByStock, setSortByStock] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saved, setSaved] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', type: 'beans', price: '', notes: '', qty: '', tag: '', intensity: '', rating: 4.5, reviews: 0 });

  const outOfStock = products.filter(p => p.qty <= 0).length;
  const lowStock = products.filter(p => p.qty > 0 && p.qty <= LOW_STOCK_THRESHOLD).length;
  const totalValue = products.reduce((s, p) => s + p.price, 0).toFixed(0);
  const inventoryValue = products.reduce((s, p) => s + p.price * p.qty, 0);

  const filtered = useMemo(() => {
    let list = products.filter(p => {
      const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.origin || '').toLowerCase().includes(search.toLowerCase());
      const matchType = filterType === 'all' || p.type === filterType;
      const matchStatus = filterStatus === 'all' || stockStatus(p.qty) === filterStatus;
      return matchSearch && matchType && matchStatus;
    });
    if (sortByStock) list = [...list].sort((a, b) => a.qty - b.qty);
    return list;
  }, [products, search, filterType, filterStatus, sortByStock]);

  const startEdit = (p) => {
    setEditId(p.id);
    setEditForm({ price: p.price, qty: p.qty, tag: p.tag || '', intensity: p.intensity || '', notes: p.notes || '' });
  };

  const saveEdit = (id) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const parsedQty = Number(editForm.qty);
      const qty = Number.isFinite(parsedQty) ? Math.max(0, Math.round(parsedQty)) : p.qty;
      return {
        ...p,
        price: Number(editForm.price) || p.price,
        qty,
        stock: qty > 0,
        tag: editForm.tag || null,
        intensity: editForm.intensity ? Number(editForm.intensity) : p.intensity,
        notes: editForm.notes || p.notes,
      };
    }));
    setEditId(null);
    setSaved(s => ({ ...s, [id]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 2000);
  };

  const adjustQty = (id, delta) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const qty = Math.max(0, p.qty + delta);
      return { ...p, qty, stock: qty > 0 };
    }));
  };

  const deleteProduct = (id) => {
    if (window.confirm('Remove this product?')) {
      setProducts(prev => prev.filter(p => p.id !== id));
    }
  };

  const addProduct = () => {
    const id = 'new-' + Date.now();
    const qty = Math.max(0, Math.round(Number(newProduct.qty)) || 0);
    setProducts(prev => [...prev, {
      ...newProduct,
      id,
      price: Number(newProduct.price) || 0,
      qty,
      stock: qty > 0,
      intensity: newProduct.intensity ? Number(newProduct.intensity) : null,
      rating: 4.5,
      reviews: 0,
      slug: newProduct.name.toLowerCase().replace(/\s+/g, '-'),
      origin: '',
    }]);
    setShowAddModal(false);
    setNewProduct({ name: '', type: 'beans', price: '', notes: '', qty: '', tag: '', intensity: '', rating: 4.5, reviews: 0 });
  };

  const typeOptions = ['all', ...Array.from(new Set(data.products.map(p => p.type)))];

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="admin-header">
          <div>
            <span className="eyebrow">Admin</span>
            <h2>Inventory & Products</h2>
          </div>
          <button className="admin-close" onClick={onClose}>
            <Icon name="close" className="icon icon--sm" />
          </button>
        </div>

        {/* Stats row */}
        <div className="admin-stats">
          <StatCard label="Total Products" value={products.length} />
          <StatCard label="Out of Stock" value={outOfStock} color={outOfStock > 0 ? 'var(--error)' : undefined} sub={outOfStock > 0 ? 'needs attention' : 'all good'} />
          <StatCard label="Low Stock" value={lowStock} color={lowStock > 0 ? 'var(--accent)' : undefined} sub={`≤ ${LOW_STOCK_THRESHOLD} units`} />
          <StatCard label="Avg Price" value={`$${(products.reduce((s, p) => s + p.price, 0) / products.length).toFixed(0)}`} />
          <StatCard label="Catalog Value" value={`$${totalValue}`} sub="sum of list prices" />
          <StatCard label="Inventory Value" value={`$${inventoryValue.toLocaleString()}`} sub="price × qty on hand" />
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          {TABS.map(t => (
            <button key={t} className={`admin-tab ${tab === t ? 'is-active' : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        {/* =================== INVENTORY TAB =================== */}
        {tab === 'Inventory' && (
          <div className="admin-content">
            <div className="admin-toolbar">
              <div className="admin-search-wrap">
                <Icon name="search" className="icon icon--sm" />
                <input
                  className="admin-search"
                  placeholder="Search products…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="admin-filters">
                {typeOptions.map(t => (
                  <button
                    key={t}
                    className={`admin-filter-btn ${filterType === t ? 'is-active' : ''}`}
                    onClick={() => setFilterType(t)}
                  >
                    {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
              <div className="admin-filters">
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s.key}
                    className={`admin-filter-btn ${filterStatus === s.key ? 'is-active' : ''}`}
                    onClick={() => setFilterStatus(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <button
                className={`admin-filter-btn ${sortByStock ? 'is-active' : ''}`}
                onClick={() => setSortByStock(s => !s)}
              >
                {sortByStock ? '✓ ' : ''}Low stock first
              </button>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Intensity</th>
                    <th>Tag</th>
                    <th>Stock</th>
                    <th>Rating</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className={p.qty <= 0 ? 'admin-row--oos' : ''}>
                      <td>
                        <div className="admin-prod-cell">
                          <div className="admin-prod-thumb">
                            <div className="admin-prod-thumb-stripe" />
                          </div>
                          <div>
                            <div className="admin-prod-name">{p.name}</div>
                            <div className="admin-prod-origin">{p.origin}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="admin-type-chip">{p.type}</span></td>
                      <td>
                        {editId === p.id ? (
                          <input
                            className="admin-inline-input"
                            type="number"
                            value={editForm.price}
                            onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                            style={{ width: 70 }}
                          />
                        ) : (
                          <span className="admin-price">${p.price}</span>
                        )}
                      </td>
                      <td>
                        {editId === p.id ? (
                          <input
                            className="admin-inline-input"
                            type="number"
                            min="1" max="12"
                            value={editForm.intensity}
                            onChange={e => setEditForm(f => ({ ...f, intensity: e.target.value }))}
                            style={{ width: 50 }}
                            placeholder="1-12"
                          />
                        ) : (
                          p.intensity ? (
                            <div className="admin-intensity-mini">
                              {Array.from({ length: 12 }).map((_, i) => (
                                <span key={i} className={`admin-int-pip ${i < p.intensity ? 'on' : ''}`} />
                              ))}
                              <span style={{ fontSize: 10, color: 'var(--ink-muted)', marginLeft: 4 }}>{p.intensity}</span>
                            </div>
                          ) : <span style={{ color: 'var(--ink-faint)', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td>
                        {editId === p.id ? (
                          <select
                            className="admin-inline-input"
                            value={editForm.tag}
                            onChange={e => setEditForm(f => ({ ...f, tag: e.target.value }))}
                          >
                            <option value="">None</option>
                            {['Bestseller', 'New', 'Limited', 'Sale'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        ) : (
                          p.tag ? <span className="admin-tag-chip">{p.tag}</span> : <span style={{ color: 'var(--ink-faint)', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td>
                        {editId === p.id ? (
                          <input
                            className="admin-inline-input"
                            type="number"
                            min="0"
                            value={editForm.qty}
                            onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))}
                            style={{ width: 60 }}
                          />
                        ) : (
                          <div className="admin-stock-cell">
                            <div className="admin-qty-stepper">
                              <button onClick={() => adjustQty(p.id, -1)} disabled={p.qty <= 0}>−</button>
                              <span>{p.qty}</span>
                              <button onClick={() => adjustQty(p.id, 1)}>+</button>
                            </div>
                            <StockBadge qty={p.qty} />
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="admin-rating">★ {p.rating.toFixed(1)}</span>
                        <span className="admin-reviews">({p.reviews})</span>
                      </td>
                      <td>
                        <div className="admin-actions">
                          {editId === p.id ? (
                            <>
                              <button className="admin-btn admin-btn--save" onClick={() => saveEdit(p.id)}>Save</button>
                              <button className="admin-btn admin-btn--cancel" onClick={() => setEditId(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              {saved[p.id] && <span className="admin-saved">✓ Saved</span>}
                              <button className="admin-btn admin-btn--edit" onClick={() => startEdit(p)}>Edit</button>
                              <button className="admin-btn admin-btn--delete" onClick={() => deleteProduct(p.id)}>Delete</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* =================== PRODUCTS TAB =================== */}
        {tab === 'Products' && (
          <div className="admin-content">
            <div className="admin-toolbar">
              <button className="bw-btn bw-btn--primary bw-btn--small" onClick={() => setShowAddModal(true)}>
                <Icon name="plus" className="icon icon--sm" /> Add Product
              </button>
              <span style={{ fontSize: 13, color: 'var(--ink-muted)' }}>{products.length} products total</span>
            </div>

            {/* Out of stock alert */}
            {outOfStock > 0 && (
              <div className="admin-alert">
                <Icon name="close" className="icon icon--sm" style={{ color: 'var(--error)' }} />
                <span><strong>{outOfStock} products</strong> are currently out of stock. Adjust quantities in the Inventory tab.</span>
              </div>
            )}

            {/* Category breakdown */}
            <div className="admin-breakdown">
              {data.categories.map(c => {
                const count = products.filter(p => p.type === c.id).length;
                const oos = products.filter(p => p.type === c.id && p.qty <= 0).length;
                return (
                  <div key={c.id} className="admin-breakdown-row">
                    <span className="admin-breakdown-name">{c.name}</span>
                    <div className="admin-breakdown-bar-wrap">
                      <div className="admin-breakdown-bar" style={{ width: `${(count / products.length) * 100}%` }} />
                    </div>
                    <span className="admin-breakdown-count">{count} products</span>
                    {oos > 0 && <span className="admin-breakdown-oos">{oos} OOS</span>}
                  </div>
                );
              })}
            </div>

            {/* Price ranges per category */}
            <div className="admin-price-table">
              <h3>Price ranges by category</h3>
              <table className="admin-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Avg</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map(c => {
                    const ps = products.filter(p => p.type === c.id);
                    if (ps.length === 0) return null;
                    const prices = ps.map(p => p.price);
                    const avg = (prices.reduce((s, v) => s + v, 0) / prices.length).toFixed(0);
                    return (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>${Math.min(...prices)}</td>
                        <td>${Math.max(...prices)}</td>
                        <td>${avg}</td>
                        <td>{ps.length}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* =================== TAGS TAB =================== */}
        {tab === 'Tags & Labels' && (
          <div className="admin-content">
            <div className="admin-tags-grid">
              {['Bestseller', 'New', 'Limited', 'Sale', null].map(tag => {
                const tagged = products.filter(p => p.tag === tag);
                return (
                  <div key={tag || 'none'} className="admin-tag-section">
                    <div className="admin-tag-section-head">
                      <span className={`admin-tag-chip ${!tag ? 'none' : ''}`}>{tag || 'No tag'}</span>
                      <span className="admin-tag-count">{tagged.length} products</span>
                    </div>
                    <div className="admin-tag-list">
                      {tagged.map(p => (
                        <div key={p.id} className="admin-tag-item">
                          <span className="admin-tag-item-name">{p.name}</span>
                          <div className="admin-tag-item-actions">
                            {['Bestseller', 'New', 'Limited', 'Sale'].filter(t => t !== tag).map(t => (
                              <button
                                key={t}
                                className="admin-tag-switch-btn"
                                onClick={() => setProducts(prev => prev.map(pr => pr.id === p.id ? { ...pr, tag: t } : pr))}
                              >
                                → {t}
                              </button>
                            ))}
                            {tag && (
                              <button
                                className="admin-tag-switch-btn admin-tag-switch-btn--remove"
                                onClick={() => setProducts(prev => prev.map(pr => pr.id === p.id ? { ...pr, tag: null } : pr))}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {tagged.length === 0 && (
                        <span style={{ fontSize: 12, color: 'var(--ink-faint)', padding: '8px 0', display: 'block' }}>No products</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Product Modal */}
        {showAddModal && (
          <div className="admin-modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-head">
                <h3>Add Product</h3>
                <button onClick={() => setShowAddModal(false)}><Icon name="close" className="icon icon--sm" /></button>
              </div>
              <div className="admin-modal-body">
                <div className="bw-form-row">
                  <div className="bw-field">
                    <label>Name</label>
                    <input value={newProduct.name} onChange={e => setNewProduct(n => ({ ...n, name: e.target.value }))} placeholder="Product name" />
                  </div>
                  <div className="bw-field">
                    <label>Type</label>
                    <select value={newProduct.type} onChange={e => setNewProduct(n => ({ ...n, type: e.target.value }))}>
                      {['beans', 'pods', 'machines', 'gear', 'drinkware'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="bw-form-row">
                  <div className="bw-field">
                    <label>Price ($)</label>
                    <input type="number" value={newProduct.price} onChange={e => setNewProduct(n => ({ ...n, price: e.target.value }))} placeholder="0" />
                  </div>
                  <div className="bw-field">
                    <label>Intensity (1–12)</label>
                    <input type="number" min="1" max="12" value={newProduct.intensity} onChange={e => setNewProduct(n => ({ ...n, intensity: e.target.value }))} placeholder="optional" />
                  </div>
                </div>
                <div className="bw-field" style={{ marginBottom: 12 }}>
                  <label>Flavor notes</label>
                  <input value={newProduct.notes} onChange={e => setNewProduct(n => ({ ...n, notes: e.target.value }))} placeholder="e.g. Jasmine, bergamot, white peach" />
                </div>
                <div className="bw-form-row">
                  <div className="bw-field">
                    <label>Tag</label>
                    <select value={newProduct.tag} onChange={e => setNewProduct(n => ({ ...n, tag: e.target.value }))}>
                      <option value="">None</option>
                      {['Bestseller', 'New', 'Limited', 'Sale'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="bw-field">
                    <label>Starting quantity</label>
                    <input type="number" min="0" value={newProduct.qty} onChange={e => setNewProduct(n => ({ ...n, qty: e.target.value }))} placeholder="0" />
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="bw-btn bw-btn--ghost bw-btn--small" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button
                  className="bw-btn bw-btn--primary bw-btn--small"
                  onClick={addProduct}
                  disabled={!newProduct.name || !newProduct.price}
                >
                  Add product
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}